import { Logo } from '@/components/logo';
import { ErrorBoundary } from '@/components/error-boundary';
import { createBrowserRouter, Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import { Menu, SquarePen } from 'lucide-react';
import { useAuthStore } from './stores/auth';
import { useMailStore } from './stores/mail';
import { useUIStore } from './stores/ui';
import { Sidebar } from './components/sidebar';
import { ConnectionBanner } from './features/connection';
import { LoginRoute } from './routes/login';
import { ForgotRoute } from './routes/forgot';
import { ResetRoute } from './routes/reset';
import { OnboardingRoute } from './routes/onboarding';
import { InboxRoute } from './routes/inbox';
import { UnifiedInboxRoute } from './routes/unified-inbox';
import { ComposeRoute } from './routes/compose';
import { ComposePopoutRoute } from './routes/compose-popout';
import { SettingsRoute } from './routes/settings';
import { RecentlyDeletedRoute } from './routes/recently-deleted';
import { SentRoute } from './routes/sent';
import { DraftsRoute } from './routes/drafts';

function AuthGate({ children }: { children: React.ReactNode }) {
  const user = useAuthStore((s) => s.user);
  const location = useLocation();

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  return <>{children}</>;
}

function MobileTopBar() {
  const { toggleMobileSidebar } = useUIStore();
  const navigate = useNavigate();
  return (
    <div className="relative flex items-center justify-between border-b border-bmail-border bg-bmail-surface px-4 py-3 md:hidden">
      <button
        onClick={toggleMobileSidebar}
        className="relative z-10 rounded-lg p-2 text-bmail-text hover:bg-bmail-surface-hover"
        aria-label="Open menu"
      >
        <Menu size={20} />
      </button>
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center shrink-0 pointer-events-none">
        <Logo className="h-11 w-auto shrink-0" />
      </div>
      <button
        onClick={() => navigate('/compose')}
        className="relative z-10 rounded-lg p-2 text-bmail-gold hover:bg-bmail-surface-hover"
        aria-label="Compose"
      >
        <SquarePen size={20} />
      </button>
    </div>
  );
}

// Persistent banner when a Gmail mailbox loses its OAuth connection — the #1
// confusing failure mode ("not found" on every click). Offers one-click re-auth.
function AuthFailedBanner() {
  const { mailboxes } = useMailStore();
  const failed = mailboxes.filter((m) => m.authFailed);
  if (failed.length === 0) return null;
  const reauth = (id: string) => { window.location.href = `${import.meta.env.VITE_API_BASE_URL || '/api'}/oauth/gmail/reauth/${id}`; };
  return (
    <div className="flex flex-wrap items-center gap-2 border-b border-bmail-error/40 bg-bmail-error/10 px-4 py-2 text-sm">
      <span className="font-medium text-bmail-error">
        {failed.length === 1
          ? `${failed[0].email} lost its Google connection.`
          : `${failed.length} mailboxes lost their Google connection.`}
      </span>
      {failed.map((m) => (
        <button
          key={m.id}
          onClick={() => reauth(m.id)}
          className="rounded-lg bg-bmail-goldfill px-3 py-1 text-xs font-semibold text-bmail-on-accent hover:bg-bmail-goldfill-hover"
        >
          Reconnect {failed.length === 1 ? '' : m.email}
        </button>
      ))}
    </div>
  );
}

function RootLayout() {
  const { theme, setTheme } = useUIStore();
  const loadUser = useAuthStore((s) => s.loadUser);

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  useEffect(() => {
    setTheme(theme);
  }, [theme, setTheme]);

  // Listen for a pop-out composer asking to dock back. The pop-out window
  // saves the draft server-side first, so all we have to do is open it.
  const navigate = useNavigate();
  useEffect(() => {
    if (!('BroadcastChannel' in window)) return;
    const bc = new BroadcastChannel('bmail-compose');
    const onMsg = (ev: MessageEvent) => {
      if (ev.data?.type === 'dock-request' && ev.data.draftId) {
        navigate(`/compose?draft=${ev.data.draftId}`);
      }
    };
    bc.addEventListener('message', onMsg);
    return () => { bc.removeEventListener('message', onMsg); bc.close(); };
  }, [navigate]);

  return (
    <ErrorBoundary>
      <div className="flex h-screen w-full bg-[var(--bg)] text-[var(--text-primary)] overflow-hidden">
        <Sidebar />
        <main className="flex-1 flex flex-col min-w-0 overflow-hidden">
          <MobileTopBar />
          <AuthFailedBanner />
          <ConnectionBanner />
          <div className="flex-1 overflow-hidden">
            <Outlet />
          </div>
        </main>
      </div>
    </ErrorBoundary>
  );
}

function AuthenticatedLayout() {
  return (
    <AuthGate>
      <RootLayout />
    </AuthGate>
  );
}

export const router = createBrowserRouter([
  { path: '/login', element: <LoginRoute /> },
  { path: '/forgot', element: <ForgotRoute /> },
  { path: '/reset', element: <ResetRoute /> },
  // Pop-out composer window: deliberately OUTSIDE the authenticated layout —
  // it renders no sidebar/chrome, just the bare composer. Auth is still
  // checked inside (a logged-out window just bounces to /login).
  {
    path: '/compose-popout',
    element: (
      <AuthGate>
        <ComposePopoutRoute />
      </AuthGate>
    )
  },
  {
    path: '/',
    element: <AuthenticatedLayout />,
    children: [
      { index: true, element: <Navigate to="/inbox" replace /> },
      { path: 'inbox', element: <UnifiedInboxRoute view="all" /> },
      { path: 'inbox/review', element: <UnifiedInboxRoute view="review" /> },
      { path: 'inbox/spam', element: <UnifiedInboxRoute view="spam" /> },
      { path: 'inbox/personal', element: <UnifiedInboxRoute view="personal" /> },
      { path: 'inbox/sent', element: <SentRoute /> },
      { path: 'inbox/drafts', element: <DraftsRoute /> },
      { path: 'inbox/deleted', element: <RecentlyDeletedRoute /> },
      { path: 'inbox/:mailboxId', element: <InboxRoute /> },
      { path: 'inbox/:mailboxId/:uid', element: <InboxRoute /> },
      { path: 'compose', element: <ComposeRoute /> },
      { path: 'settings', element: <SettingsRoute /> }
    ]
  },
  { path: '/onboarding', element: <OnboardingRoute /> },
  // Catch-all: send any unknown path back to the app root (→ /inbox if signed in,
  // else /login) instead of React Router's raw "404 Not Found" developer page.
  { path: '*', element: <Navigate to="/" replace /> }
]);